'use client';

import { useState, useEffect, useRef } from 'react';
import Script from 'next/script';

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
    };
  }
}

interface ReviewFormProps {
  orderId?: string;
  customerEmail?: string;
  customerName?: string;
  productHandle?: string;
  heading?: string;
  subheading?: string;
}

export default function ReviewForm({
  orderId,
  customerEmail,
  customerName,
  productHandle = 'change-song-lyrics',
  heading,
  subheading,
}: ReviewFormProps = {}) {
  const isOrderLinked = !!orderId;
  const [rating, setRating] = useState(5);
  const [author, setAuthor] = useState(customerName || '');
  const [email, setEmail] = useState(customerEmail || '');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!turnstileSiteKey) return;
    if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => setTurnstileToken(token),
      });
    }
  }, [turnstileSiteKey]);

  function handleTurnstileLoad() {
    if (!turnstileSiteKey) return;
    if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => setTurnstileToken(token),
      });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    if (turnstileSiteKey && !turnstileToken) {
      setErrorMsg('Please complete the verification check.');
      setStatus('error');
      return;
    }

    if (/[\w.+-]+@[\w-]+\.[\w.-]+/.test(author)) {
      setErrorMsg('Please enter a name in the Name field, not an email address.');
      setStatus('error');
      return;
    }

    try {
      const res = await fetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          author,
          email,
          reviewBody: body,
          source: 'ChangeLyric',
          productHandle,
          orderId,
          token: turnstileToken,
          website: (document.getElementById('review-website') as HTMLInputElement)?.value || '',
        }),
      });

      if (res.status === 409) {
        setErrorMsg('You have already submitted a review. Thank you!');
        setStatus('error');
        return;
      }

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to submit review');
      }

      setStatus('success');
      setAuthor('');
      setEmail('');
      setBody('');
      setRating(5);
      setTurnstileToken('');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong');
      setStatus('error');
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken('');
      }
    }
  }

  if (status === 'success') {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body text-center">
          <svg className="w-12 h-12 text-success mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-bold">Thank You!</h3>
          <p className="text-base-content/70">Your review has been submitted successfully.</p>
          <button className="btn btn-ghost btn-sm mt-2" onClick={() => { setStatus('idle'); if (window.turnstile && widgetIdRef.current) { window.turnstile.reset(widgetIdRef.current); } }}>
            Write another review
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      {turnstileSiteKey && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback"
          strategy="afterInteractive"
          onLoad={handleTurnstileLoad}
        />
      )}
      <div className="card-body">
        <h3 className="card-title text-xl mb-2">{heading || 'Write a Review'}</h3>
        <p className="text-base-content/60 text-sm mb-4">
          {subheading || 'Share your experience with ChangeLyric'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot */}
          <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
            <label htmlFor="review-website">Website</label>
            <input type="text" id="review-website" name="website" tabIndex={-1} autoComplete="off" />
          </div>

          {/* Star Rating */}
          <div>
            <label className="label">
              <span className="label-text font-medium">Your Rating</span>
            </label>
            <div className="rating rating-lg">
              {[1, 2, 3, 4, 5].map((val) => (
                <input
                  key={val}
                  type="radio"
                  name="rating"
                  className="mask mask-star-2 bg-warning"
                  checked={rating === val}
                  onChange={() => setRating(val)}
                />
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="label">
              <span className="label-text font-medium">Your Name</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              placeholder="Jane D."
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              required
            />
          </div>

          {/* Email — hidden when the form is already linked to an order */}
          {isOrderLinked ? (
            <input type="hidden" value={email} readOnly />
          ) : (
            <div>
              <label className="label">
                <span className="label-text font-medium">Email</span>
              </label>
              <input
                type="email"
                className="input input-bordered w-full"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label className="label">
                <span className="label-text-alt text-base-content/50">Not displayed publicly</span>
              </label>
            </div>
          )}

          {/* Review body */}
          <div>
            <label className="label">
              <span className="label-text font-medium">Your Review</span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full h-28"
              placeholder="Tell others about your experience..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              minLength={10}
            />
          </div>

          {turnstileSiteKey && <div ref={turnstileRef} className="flex justify-center" />}

          {errorMsg && (
            <div className="alert alert-error text-sm">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{errorMsg}</span>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              'Submit Review'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
